// ./src/components/features/Themes/ThemeFormModal/ThemeGroupEditor.tsx
import React from "react";
import { Stack, Text, Divider } from "@mantine/core";
import { ThemeElementControl, ThemeElementConfig } from "./ThemeElementControl";
import { getNestedValue } from "./themeConfigUtils";

export interface ThemeGroupConfig {
  groupName: string;
  elements: ThemeElementConfig[];
}

export interface ThemeGroupEditorProps {
  /** Group configuration from themeEditorUiConfig */
  groupConfig: ThemeGroupConfig;

  /** Complete theme config object */
  themeConfigData: Record<string, any>;

  /** Callback when any element value changes */
  onConfigChange: (path: string, value: any | null) => void;

  /** Whether this is the first group (affects top spacing) */
  isFirst?: boolean;
}

export const ThemeGroupEditor: React.FC<ThemeGroupEditorProps> = ({
  groupConfig,
  themeConfigData,
  onConfigChange,
  isFirst = false,
}) => {
  const { groupName, elements } = groupConfig;

  // Don't render if no elements
  if (!elements || elements.length === 0) {
    return null;
  }

  const hasGroupName = groupName && groupName.trim().length > 0;

  return (
    <Stack gap="md">
      {/* Group header - only show if group has a name */}
      {hasGroupName && (
        <>
          {!isFirst && <Divider />}
          <Text size="sm" fw={600} tt="uppercase" c="dimmed">
            {groupName}
          </Text>
        </>
      )}

      {/* Render all elements in the group */}
      <Stack gap="lg">
        {elements.map((elementConfig, index) => {
          const value = getNestedValue(themeConfigData, elementConfig.accessAs);

          return (
            <ThemeElementControl
              key={`${elementConfig.accessAs}-${index}`}
              elementConfig={elementConfig}
              value={value}
              onChange={(newValue: any) =>
                onConfigChange(elementConfig.accessAs, newValue)
              }
            />
          );
        })}
      </Stack>
    </Stack>
  );
};
